/**
 * @fileoverview Shared label + status-pill wrapper for every widget.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render the canonical label / asterisk / help-text / N/A button /
 *   PASS-FAIL pill / hidden-banner / disabled-state / error-message
 *   chrome consistently across all 18 widgets.
 * - Surface the data-attributes the renderer's E2E + visual tests rely on
 *   (`data-field-id`, `data-widget-type`).
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` for `ResolvedFieldStateV2` (Phase 2).
 *
 * Architecture:
 * - Presentation-only. No state, no effects, no host coupling.
 * - Widgets compose: `<FieldWrapper {...wrap}>...control...</FieldWrapper>`.
 *
 * Notes:
 * - All a11y attributes from D-0.17 baseline live here so a single fix
 *   propagates everywhere.
 * - N/A is rendered as an inline button to the RIGHT of the widget control
 *   (not below it) so inspectors can flip it with a single thumb tap
 *   without breaking the visual line of the question. The button uses
 *   `pointer-events-auto` for enabled fields, while the button's disabled
 *   state follows the resolved field state so read-only views cannot change it.
 *
 * @component FieldWrapper
 *
 * Purpose: shared visual chrome around an interactive widget control.
 *
 * Props:
 * - fieldId: per-field DOM id + data-attribute anchor.
 * - widgetType: canonical FieldType marker used by E2E selectors.
 * - label: human-readable label.
 * - required: static or rule-driven required state (renders asterisk).
 * - helpText: optional descriptive subtext beneath the label.
 * - resolved: ResolvedFieldStateV2 from engine v2 (hidden / disabled / required).
 * - isNA: when true, dims the control + counts the field as answered.
 * - naEnabled: when true, surfaces the inline N/A button to the right.
 * - onToggleNA: optional handler for the N/A button click.
 * - passFailStatus: 'pass' | 'fail' | 'neutral' — drives the PASS/FAIL pill.
 * - errorMessage: optional validation error rendered under the control.
 * - children: the actual interactive control (the widget body).
 *
 * Behavior:
 * - Renders nothing when `resolved.hidden === true` (conduct + preview both).
 * - PASS / FAIL pills and the asterisk are presentational only; no state
 *   mutation happens here.
 *
 * Data Source:
 * - local (pure presentation).
 *
 * Constraints:
 * - No API calls, no Dexie writes, no host imports. Tests with raw render
 *   should not need any provider wrap.
 *
 * Performance:
 * - Re-renders only when own props change.
 *
 * @contract FieldWrapperProps
 * Purpose: input contract for the per-widget visual chrome.
 *
 * @contract PassFailStatus
 * Purpose: 'pass' | 'fail' | 'neutral' — drives the colored status pill.
 */
import { type ReactNode, useId, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Hand } from 'lucide-react';
import type { ResolvedFieldStateV2 } from '@firesquirrel/core-domain';

/**
 * @contract PassFailStatus
 * Purpose: Describes pass fail status at the shared type boundary.
 * Fields: 'pass' | 'fail' | 'neutral'
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export type PassFailStatus = 'pass' | 'fail' | 'neutral';

/**
 * @contract FieldWrapperProps
 * Purpose: Describes field wrapper props at the shared type boundary.
 * Fields: fieldId, widgetType, label, helpText, required, resolved, isNA, naEnabled, onToggleNA, passFailStatus, errorMessage, requiredUnmet, children
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export interface FieldWrapperProps {
  fieldId: string;
  widgetType: string;
  label?: string;
  helpText?: string;
  required?: boolean;
  resolved?: ResolvedFieldStateV2;
  isNA?: boolean;
  naEnabled?: boolean;
  onToggleNA?: (next: boolean) => void;
  passFailStatus?: PassFailStatus;
  /** When true, the inspection is completed or under review; all controls are disabled. */
  readOnly?: boolean;
  requiredImmediate?: boolean;
  occurrenceCount?: number;
  errorMessage?: string;
  /**
   * True when the field is required and has no value. Computed by
   * `<FieldRenderer>` from a single source of truth
   * (`validation/fieldIssues.ts`) so the badge / panel / wrapper never disagree.
   */
  requiredUnmet?: boolean;
  children: ReactNode;
}

/**
 * @component FieldWrapper
 * Purpose: Renders field wrapper for the current workflow.
 * Props: Inputs: props; detailed shape follows the declared props.
 * Behavior: Updates presentation from supplied inputs and existing local hooks.
 * Data Source: Typed props and existing local service or hook state.
 * Constraints: Persistence stays behind the service or adapter boundary.
 * Performance: Keep subscriptions and rendering scoped to current inputs.
 */
export function FieldWrapper(props: FieldWrapperProps) {
  const {
    fieldId, widgetType, label, helpText,
    required, resolved, isNA, naEnabled,
    onToggleNA, passFailStatus, readOnly, errorMessage,
    requiredUnmet, requiredImmediate, occurrenceCount = 0, children,
  } = props;

  const helpId = useId();
  const errorId = useId();
  const requiredHelpId = useId();

  // Touched state — flips on the first focus-leave inside the wrapper. Lets
  // us avoid screaming "Required" at the user before they've even tried to
  // engage with the control.
  const [touched, setTouched] = useState(false);

  // B16-aware visibility: the renderer must respect resolved.hidden coming
  // from engine v2. Returning null keeps the field out of the DOM entirely
  // for the conduct flow; preview surfaces apply a separate "hidden with data"
  // banner upstream (D-0.6 §"Per-widget rows").
  if (resolved?.hidden) return null;

  const requiredEffective = !readOnly && !!(required || resolved?.required);
  const disabled = !!resolved?.disabled || !!readOnly;
  const showRequiredIndicator = requiredEffective && !disabled;
  // Conduct mode shows the unmet ring immediately; other modes wait for touch.
  const showRequiredRing = !!requiredUnmet && (requiredImmediate || touched) && !isNA && !disabled;
  const showInvalidState = showRequiredRing || !!errorMessage;
  const ringClass = showInvalidState
    ? 'ring-1 ring-red-300 rounded-sm p-1 -m-1'
    : '';

  const showNAButton = (!!naEnabled || !!isNA) && !!onToggleNA;
  const touchStartXRef = useRef<number | null>(null);

  return (
    <div
      data-field-id={fieldId}
      data-widget-type={widgetType}
      data-required={requiredEffective ? 'true' : 'false'}
      data-is-na={isNA ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
      data-pass-fail={passFailStatus ?? 'neutral'}
      data-required-unmet={requiredUnmet ? 'true' : 'false'}
      data-touched={touched ? 'true' : 'false'}
      onTouchStart={(e) => {
        if (!showNAButton || disabled) return;
        touchStartXRef.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        if (!showNAButton || disabled || touchStartXRef.current === null) return;
        const endX = e.changedTouches[0]?.clientX;
        const deltaX = endX != null ? endX - touchStartXRef.current : 0;
        touchStartXRef.current = null;
        // Swipe left (>40px) marks question as N/A; swipe right (>40px) clears N/A
        if (deltaX < -40 && !isNA) {
          onToggleNA!(true);
        } else if (deltaX > 40 && isNA) {
          onToggleNA!(false);
        }
      }}
      onBlur={(e) => {
        // Only flip touched when focus actually leaves the wrapper (not when
        // it just moves between sub-controls of a composite widget).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setTouched(true);
        }
      }}
      aria-invalid={showInvalidState}
      className={`flex flex-col gap-1 ${isNA ? 'opacity-60 pointer-events-none' : ''} ${ringClass}`.trim()}
    >
      {label && (
        <label
          htmlFor={fieldId}
          className="text-sm font-medium text-gray-800 flex flex-wrap items-center gap-1"
        >
          <span>{label}</span>
          {showRequiredIndicator && <span aria-label="required" className="text-red-500">*</span>}
          {passFailStatus === 'pass' && (
            <span role="status" aria-label="Status: pass" data-pill="pass" className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              PASS
            </span>
          )}
          {passFailStatus === 'fail' && (
            <span role="status" aria-label="Status: fail" data-pill="fail" className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
              {occurrenceCount > 0 ? `FAIL (${occurrenceCount} Occurrences)` : 'FAIL'}
            </span>
          )}
        </label>
      )}

      {/* Widget body sits in a flex row with the inline N/A button on the
          right. The widget container takes `flex-1 min-w-0` so it shrinks
          cleanly when long labels/options would otherwise push the N/A
          button off-screen. */}
      <div className="flex items-start gap-2">
        <div
          className="flex-1 min-w-0"
          aria-describedby={[helpText ? helpId : undefined, errorMessage ? errorId : undefined, showRequiredRing ? requiredHelpId : undefined].filter(Boolean).join(' ') || undefined}
        >
          {children}
        </div>
        {showNAButton && (
          <button
            type="button"
            disabled={disabled}
            aria-disabled={disabled}
            aria-pressed={!!isNA}
            aria-label={isNA ? 'Clear N/A' : 'Mark as N/A'}
            data-pill="na"
            data-na-active={isNA ? 'true' : 'false'}
            onClick={() => onToggleNA!(!isNA)}
            className={[
              // Inline + always interactive even when the wrapper greys out
              // the rest of the field (unless disabled by readOnly/policy).
              'shrink-0 pointer-events-auto self-start',
              // Sized for a comfortable thumb-tap on mobile (~36px tall,
              // ~44px wide). The user is allowed to flip N/A from any state.
              'inline-flex items-center justify-center gap-1 min-w-[44px] h-9 px-2.5',
              'text-xs font-semibold rounded-sm border transition-colors',
              disabled
                ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200'
                : isNA
                  ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
            ].join(' ')}
          >
            <span aria-hidden="true" className="inline-flex items-center -space-x-0.5">
              {isNA ? <ArrowRight className="w-3 h-3" /> : <ArrowLeft className="w-3 h-3" />}
              <Hand className="w-3.5 h-3.5" />
            </span>
            <span>N/A</span>
          </button>
        )}
      </div>

      {helpText && (
        <p id={helpId} className="text-xs text-gray-500">{helpText}</p>
      )}
      {errorMessage && (
        <p id={errorId} role="alert" className="text-xs text-red-600">{errorMessage}</p>
      )}
      {showRequiredRing && (
        <p
          id={requiredHelpId}
          role="alert"
          data-required-helper="true"
          className="text-xs text-red-600"
        >
          Required — enter a value
        </p>
      )}
    </div>
  );
}
